import React, { useMemo, useState, useEffect, useContext, useCallback } from "react";
import ResizableHeader from "../../shared/ResizableHeader";
import TablePagination from "../../shared/TablePagination";
import useAutoAdvancePagination from "../../shared/useAutoAdvancePagination";
import ExportLoader from "../../shared/ExportLoader";
import useReportExport from "../../shared/useReportExport";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { AuthContext } from "../../../context/AuthContext";
import { useDbContext } from "../../../context/DbContext";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../NavBars/ActionToolbar";
import GridDesign from "../../GridDesign/GridDesign";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";

// ─── Default column definitions ───────────────────────────────────────────────
const getDefaultColumns = () => {
  const list = [
    { key: "accno",     label: "AccNo",     width: 100 },
    { key: "customer",  label: "Customer",  width: 180 },
    { key: "entryno",   label: "Entryno",   width: 100, align: "center" },
    { key: "type",      label: "Type",      width: 100 },
    { key: "rate",      label: "Rate",      width: 90,  align: "right" },
    { key: "amount",    label: "Amount",    width: 110, align: "right" },
    { key: "date",      label: "Date",      width: 100, align: "center" },
    { key: "weight",    label: "Weight",    width: 90,  align: "right" },
    { key: "agent",     label: "Agent",     width: 120 },
    { key: "narration", label: "Narration", width: 180 },
  ];
  return list.map((col, idx) => ({
    ...col,
    columnName: col.key,
    header:     col.label,
    orderNo:    idx,
    show:       true,
  }));
};

const FORM_NAME = "Receipt List Report";

function ReceiptListReport({ onClose, reportData, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const { agentCode } = useContext(AuthContext);

  const [tableData,           setTableData]           = useState([]);
  const [isLoading,           setIsLoading]           = useState(true);
  const [errorMsg,            setErrorMsg]            = useState("");
  const [searchQuery,         setSearchQuery]         = useState("");
  const [gridColumns,         setGridColumns]         = useState(() => getDefaultColumns());
  const [showGridDesignModal, setShowGridDesignModal] = useState(false);
  const [isRefreshing,        setIsRefreshing]        = useState(false);

  // ── Load grid column settings ──────────────────────────────────────────────
  useEffect(() => {
    const loadGridSettings = async () => {
      if (!dbCode) return;
      const defaults = getDefaultColumns();
      const cached = localStorage.getItem(`grid_design_${FORM_NAME}`);
      if (cached) {
        try { setGridColumns(JSON.parse(cached)); return; } catch (e) {}
      }

      try {
        const res = await axios.get(`${apiBaseUrl}/main/griddesign/${dbCode}/${FORM_NAME}`);
        const dbCols = res.data.recordset || res.data || [];
        if (dbCols.length > 0) {
          const merged = defaults.map((initCol) => {
            const m = dbCols.find(
              (c) => String(c.ColName).toLowerCase() === String(initCol.columnName).toLowerCase()
            );
            return m
              ? { ...initCol, width: m.Width ?? initCol.width, orderNo: m.InNo ?? initCol.orderNo, header: m.Header || initCol.header, show: m.Vis === 1 }
              : initCol;
          });
          merged.sort((a, b) => a.orderNo - b.orderNo);
          localStorage.setItem(`grid_design_${FORM_NAME}`, JSON.stringify(merged));
          setGridColumns(merged);
        } else {
          setGridColumns(defaults);
        }
      } catch {
        setGridColumns(defaults);
      }
    };
    loadGridSettings();
  }, [dbCode, apiBaseUrl]);

  // ── Visible columns & grid template ───────────────────────────────────────
  const visibleColumns = useMemo(() => gridColumns.filter((col) => col.show), [gridColumns]);
  const gridTemplateColumns = useMemo(
    () => visibleColumns.map((col) => `${col.width}px`).join(" "),
    [visibleColumns]
  );

  // ── Fetch report data ──────────────────────────────────────────────────────
  const fetchData = async () => {
    try {
      setIsLoading(true);
      setErrorMsg("");

      // Map the object payloads back to primitive values for the backend API
      const payload = {
        fromDate: reportData.fromDate,
        toDate: reportData.toDate,
        reportType: reportData.reportType?.value || reportData.reportType || "Simple",
        // The backend `schemereceipt.service.js` expects these to be arrays (using [0] or [1])
        customer: reportData.customer?.value ? [reportData.customer.value, reportData.customer.label] : "",
        agent: reportData.agent?.value ? [reportData.agent.value, reportData.agent.label] : "",
        accno: reportData.accno?.value ? [reportData.accno.value, reportData.accno.label] : "",
        mobileNo: reportData.mobileNo || "",
        fyid: reportData.fyid || 0,
        locationId: reportData.locationId !== undefined ? Number(reportData.locationId) : 0,
      };

      const response = await axios.get(`${apiBaseUrl}/main/schReceiptList/${dbCode}`, {
        params: payload,
      });
      setTableData(response.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setErrorMsg("Error connecting to server. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (dbCode) fetchData();
  }, [apiBaseUrl, reportData, dbCode]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  // ── Number formatter ───────────────────────────────────────────────────────
  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined || value === "") return "";
    const n = Number(value);
    if (isNaN(n)) return "";
    return n.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  // ── Row value mapper ───────────────────────────────────────────────────────
  const formatValue = (row, key) => {
    switch (key) {
      case "accno":     return row.accno || "";
      case "customer":  return row.Cust || "";
      case "entryno":   return row.Srp_entryno ? `${row.Srp_entryno}-${row.agent ? row.agent.substring(0, 3) : ""}` : "";
      case "type":      return row.Type || "";
      case "rate":      return row.SrInfo_rate || "";
      case "amount":    return formatNumber(row.Srp_amount);
      case "date":      return row.SrInfo_date || "";
      case "weight":    return formatNumber(row.Srp_gm, 3);
      case "agent":     return row.agent || "";
      case "narration": return row.Srp_narration || "";
      default:          return "";
    }
  };

  // ── Client-side quick search ───────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return tableData;
    const query = searchQuery.toLowerCase().trim();
    return tableData.filter((row) =>
      visibleColumns.some((col) =>
        String(formatValue(row, col.key)).toLowerCase().includes(query)
      )
    );
  }, [tableData, searchQuery, visibleColumns]);

  // ── Dynamic footer totals based on filtered rows ───────────────────────────
  const numericKeys = ["amount", "weight"];

  const dynamicTotals = useMemo(() => {
    return filteredRows.reduce((acc, row) => {
      numericKeys.forEach((key) => {
        // Exclude subtotals and totals from dynamic aggregation so we don't double count
        if (row.Type !== "Sub Total" && row.Type !== "Total") {
          let valStr = "";
          if (key === "amount") valStr = row.Srp_amount;
          if (key === "weight") valStr = row.Srp_gm;
          
          const v = parseFloat(valStr);
          acc[key] = (acc[key] || 0) + (isNaN(v) ? 0 : v);
        }
      });
      return acc;
    }, {});
  }, [filteredRows]);

  // ==================== CONTINUOUS INFINITE SCROLL & KEYBOARD NAV ====================
  const {
    currentPage,
    pageSize,
    pageSizeOptions,
    totalPages,
    totalRows,
    displayedRows,
    selectedIndex,
    setSelectedIndex,
    tableContainerRef,
    handleScroll,
    handleWheel,
    handleKeyDown,
    handlePageChange,
    handlePageSizeChange,
    autoAdvance,
    toggleAutoAdvance,
    isFlipping,
  } = useAutoAdvancePagination({
    items: filteredRows,
    initialPageSize: 100,
    pageSizeOptions: [50, 100, 250, 500],
    initialAutoAdvance: true,
  });

  const { exportingText, runExport } = useReportExport();

  // ── Export CSV ─────────────────────────────────────────────────────────────
  const exportToCsv = () => {
    runExport("Generating Excel / CSV...", () => {
      const header = visibleColumns.map((col) => col.header).join(",");
      const rows = filteredRows.map((row) =>
        visibleColumns
          .map((col) => `"${String(formatValue(row, col.key)).replace(/"/g, '""')}"`)
          .join(",")
      );
      const totalRow = visibleColumns.map((col) => {
        if (col.key === "customer") return '"Total"';
        if (col.key === "amount") return `"${formatNumber(dynamicTotals.amount)}"`;
        if (col.key === "weight") return `"${formatNumber(dynamicTotals.weight, 3)}"`;
        return '""';
      }).join(",");

      const csv = [header, ...rows, totalRow].join("\n");
      const link = document.createElement("a");
      link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
      link.download = "receipt_list_report.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  // ── Export PDF (upgraded from html2canvas to jsPDF-autotable) ──────────────
  const exportToFullPDF = () => {
    runExport("Generating PDF Report...", () => {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });
      const columns = visibleColumns.map((col, idx) => ({ header: col.header, dataKey: `col${idx + 1}` }));
      const rows = filteredRows.map((item) => {
        const row = {};
        visibleColumns.forEach((col, idx) => { row[`col${idx + 1}`] = formatValue(item, col.key); });
        return row;
      });
      
      // Total Row
      const totalRow = {};
      visibleColumns.forEach((col, idx) => {
        if (col.key === "customer") totalRow[`col${idx + 1}`] = "Total";
        else if (col.key === "amount") totalRow[`col${idx + 1}`] = formatNumber(dynamicTotals.amount);
        else if (col.key === "weight") totalRow[`col${idx + 1}`] = formatNumber(dynamicTotals.weight, 3);
        else totalRow[`col${idx + 1}`] = "";
      });
      rows.push(totalRow);

      doc.text("Receipt List Report", 15, 20);
      doc.autoTable({ columns, body: rows, startY: 30, margin: { top: 30 }, styles: { fontSize: 8 } });
      doc.save("receipt_list_report.pdf");
    });
  };

  // Stable column resize handler (shared ResizableHeader imported at module scope)
  const handleColumnResize = useCallback((key, newWidth, name) => {
    setGridColumns((prev) => {
      const updated = prev.map((c) => (c.key === key ? { ...c, width: newWidth } : c));
      localStorage.setItem(`grid_design_${name}`, JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <div
      className="bg-white mt-[2vh] sm:mt-0 font-normal w-[98vw] h-[96vh] rounded-[5px] shadow-md flex flex-col items-center absolute transition-all duration-300 md:w-[98%] md:h-[98%]"
      style={{ zIndex }}
    >
      <style>{`
        .lr-border { border: 1px solid #ffffff; }
        .report-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
        .report-scroll::-webkit-scrollbar-track { background: #f1f1f1; }
        .report-scroll::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
        .report-scroll::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
      `}</style>

      {/* ── Window Header ── */}
      <WindowHeader
        onClose={onClose}
        title="Receipt List Report"
        logo={headeLogo}
        style={{ height: "30px" }}
        className="bg-[#0f56d3] text-white"
        titleClassName="text-xs"
        closeBtnClassName="w-6 h-6 rounded-[5px] border-none mr-2 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white"
      />

      {/* ── Action Toolbar ── */}
      <ActionToolbar
        size="small"
        iconSize={16}
        buttonHeight="36px"
        showSave={false}
        showPrint
        showClear={false}
        showFind={false}
        showEdit={false}
        showDelete={false}
        showExit
        showPdf
        showExcel
        showSettingsBar
        showRefresh
        showNavigation={false}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        onPrint={() => window.print()}
        onExcel={exportToCsv}
        onPdf={exportToFullPDF}
        onExit={onClose}
        settingsOptions={[
          { label: "Design Columns", onClick: () => setShowGridDesignModal(true) },
        ]}
      />

      {/* ── Body ── */}
      <div className="w-full flex-1 min-h-0 flex flex-col bg-white">

        {/* Info panel + Search bar */}
        <div className="w-full border-b border-gray-200 bg-gradient-to-r from-slate-50 to-blue-50/20 px-4 py-2 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] font-['Inter'] text-slate-700 flex-1">

            {/* Period */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
              <svg className="w-3.5 h-3.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="font-semibold text-slate-500">Period:</span>
              <span className="font-medium text-slate-900">
                {reportData?.fromDate || "-"} to {reportData?.toDate || "-"}
              </span>
            </div>

            {/* Customer */}
            {reportData?.customer?.label && (
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
                <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="font-semibold text-slate-500">Customer:</span>
                <span className="font-medium text-indigo-700">{reportData.customer.label}</span>
              </div>
            )}

            {/* Report Type */}
            {reportData?.reportType?.label && (
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
                <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H3a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2v14a2 2 0 01-2 2z" />
                </svg>
                <span className="font-semibold text-slate-500">Type:</span>
                <span className="font-medium text-emerald-700">{reportData.reportType.label}</span>
              </div>
            )}

            {/* Agent */}
            {reportData?.agent?.label && (
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 shadow-sm px-2.5 py-1 rounded-[6px]">
                <svg className="w-3.5 h-3.5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="font-semibold text-slate-500">Agent:</span>
                <span className="font-medium text-amber-700">{reportData.agent.label}</span>
              </div>
            )}

          </div>

          {/* Quick Search */}
          <div className="flex items-center shrink-0">
            <div className="relative w-64">
              <input
                type="text"
                placeholder="Search across all columns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-7 py-1.5 text-xs border border-slate-300 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-['Inter'] font-semibold text-slate-800 bg-white shadow-sm transition-all placeholder-slate-400"
              />
              <svg className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600 text-xs font-semibold px-1"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Grid and Loader */}
        <div
          ref={tableContainerRef}
          className="w-full flex-1 min-h-0 overflow-auto text-black text-xs font-semibold report-scroll outline-none focus:ring-1 focus:ring-blue-400"
          tabIndex={0}
          onScroll={handleScroll}
          onWheel={handleWheel}
          onKeyDown={handleKeyDown}
        >
          {isLoading ? (
            <div className="w-full h-full flex items-center justify-center bg-white/70">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-4 border-[#0f56d3] border-t-transparent rounded-full animate-spin" />
                <span className="font-['Inter'] text-[13px] text-gray-600 font-medium">Fetching Report Data...</span>
              </div>
            </div>
          ) : errorMsg ? (
            <div className="w-full h-full flex items-center justify-center bg-white">
              <span className="font-['Inter'] text-[13px] text-red-500 font-medium">{errorMsg}</span>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="w-full h-full flex items-center justify-center bg-white">
              <span className="font-['Inter'] text-[13px] text-gray-400 font-medium">No records found for the selected filters.</span>
            </div>
          ) : (
            <div className="min-w-max flex flex-col min-h-full">
              {/* Header Row */}
              <div className="sticky top-0 z-10 bg-blue-600 lr-border shrink-0">
                <div className="grid gap-0 h-8" style={{ gridTemplateColumns }}>
                  {visibleColumns.map((col) => (
                    <div
                      key={col.key}
                      className="text-white truncate text-center font-semibold font-['Inter'] lr-border text-xs w-full h-full flex items-center justify-center"
                    >
                      <ResizableHeader col={col} formName={FORM_NAME} onResize={handleColumnResize}>{col.header}</ResizableHeader>
                    </div>
                  ))}
                </div>
              </div>

              {/* Data Rows */}
              <div className="bg-gray-100 flex-1">
                {displayedRows.map((row, rowIndex) => {
                  const isSelected = rowIndex === selectedIndex;
                  const isSubTotal = row.Type === "Sub Total";
                  const isTotal    = row.Type === "Total";
                  return (
                    <div
                      key={rowIndex}
                      data-row-index={rowIndex}
                      onClick={() => setSelectedIndex(rowIndex)}
                      className={`grid gap-0 h-8 border-b border-gray-300 hover:bg-blue-50/40 cursor-pointer transition-colors duration-150 ${
                        isSelected
                          ? "bg-blue-100 font-bold text-blue-900 ring-1 ring-inset ring-blue-500"
                          : isSubTotal
                          ? "bg-red-50 text-red-700 font-bold"
                          : isTotal
                            ? "bg-[#dbeafe] text-blue-800 font-bold"
                            : rowIndex % 2 === 0
                              ? "bg-[#f7f7f7]"
                              : "bg-[#eeeeee]"
                      }`}
                      style={{ gridTemplateColumns }}
                    >
                      {visibleColumns.map((col) => (
                        <div
                          key={col.key}
                          className={`font-['Inter'] text-[11px] font-medium lr-border px-2 flex items-center truncate ${
                            col.align === "right"
                              ? "justify-end text-right"
                              : col.align === "center"
                                ? "justify-center text-center"
                                : ""
                          }`}
                        >
                          {formatValue(row, col.key)}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Footer Totals Row */}
              <div className="grid gap-0 h-8 bg-[#dbeafe] text-blue-800 font-bold sticky bottom-0 z-10 shrink-0" style={{ gridTemplateColumns }}>
                {visibleColumns.map((col, i) => {
                  const isNum = numericKeys.includes(col.key);
                  return (
                    <div
                      key={col.key}
                      className={`font-['Inter'] text-[11px] lr-border px-2 flex items-center truncate ${
                        isNum ? "justify-end text-right" : i === 0 ? "justify-start" : ""
                      }`}
                    >
                      {i === 0
                        ? "Overall Total"
                        : isNum
                          ? formatNumber(dynamicTotals[col.key] || 0, col.key === "weight" ? 3 : 2)
                          : ""}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <TablePagination
          currentPage={currentPage}
          totalRows={totalRows}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          autoAdvance={autoAdvance}
          onToggleAutoAdvance={toggleAutoAdvance}
          isFlipping={isFlipping}
        />
      </div>

      {/* Grid Design Modal */}
      {showGridDesignModal && (
        <GridDesign
          onClose={() => setShowGridDesignModal(false)}
          onSave={(updated) => {
            setGridColumns(updated);
            setShowGridDesignModal(false);
          }}
          formName={FORM_NAME}
          initialColumns={getDefaultColumns()}
          zIndex={zIndex}
        />
      )}

      {/* Global Export Loader for Heavy Operations */}
      <ExportLoader message={exportingText} />
    </div>
  );
}

export default ReceiptListReport;